import React from 'react';
import { Link } from 'react-router-dom';
import { Download, Phone, Mail, MapPin, Building2, Award, Users, CheckCircle2 } from 'lucide-react';

const CompanyProfile = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="company-profile-page">
      {/* Download Button - Hidden in Print */}
      <div className="no-print" style={{ 
        position: 'fixed', 
        top: '100px', 
        right: '30px', 
        zIndex: 1000 
      }}>
        <button
          onClick={handlePrint}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '14px 28px',
            background: 'var(--brand-teal)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontFamily: "'Suisse Medium', sans-serif",
            fontSize: '14px',
            boxShadow: '0 4px 12px rgba(0, 128, 128, 0.3)'
          }}
        >
          <Download size={20} />
          Download PDF
        </button>
      </div>

      {/* Profile Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px', borderBottom: '3px solid var(--brand-teal)', paddingBottom: '32px' }}>
          <img 
            src="/pinnacle-logo.png" 
            alt="Pinnacle Biomed Logo" 
            style={{ height: '120px', marginBottom: '16px' }}
          />
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '600', 
            color: 'var(--brand-teal)',
            marginBottom: '8px'
          }}>
            PINNACLE BIOMED PVT. LTD.
          </h1>
          <p style={{ 
            fontSize: '18px', 
            color: 'var(--brand-orange)',
            fontStyle: 'italic'
          }}>
            Affecting Lives.. Positively.
          </p>
        </div>

        {/* Company Overview */}
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            color: 'var(--brand-teal)', 
            borderBottom: '2px solid var(--brand-orange)',
            paddingBottom: '8px',
            marginBottom: '16px'
          }}>
            Company Overview
          </h2>
          <p style={{ lineHeight: '1.8', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Pinnacle Biomed India Pvt. Ltd. is a leading turnkey healthcare infrastructure and hospital management company providing comprehensive end-to-end solutions for Government, Private, and Trust Hospitals/Institutions across India.
          </p>
          <p style={{ lineHeight: '1.8', color: 'var(--text-secondary)' }}>
            We undertake single-agency responsibility for projects from conceptualization to commissioning and long-term operations, ensuring compliance with statutory, medical, and health safety standards.
          </p>
        </section>

        {/* Key Statistics */}
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            color: 'var(--brand-teal)', 
            borderBottom: '2px solid var(--brand-orange)',
            paddingBottom: '8px',
            marginBottom: '16px'
          }}>
            Key Highlights
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            <div style={{ textAlign: 'center', padding: '20px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ fontSize: '28px', fontWeight: '600', color: 'var(--brand-teal)' }}>3600+</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Bed Capacity</div>
            </div>
            <div style={{ textAlign: 'center', padding: '20px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ fontSize: '28px', fontWeight: '600', color: 'var(--brand-teal)' }}>2500+</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Temporary Beds</div>
            </div>
            <div style={{ textAlign: 'center', padding: '20px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ fontSize: '28px', fontWeight: '600', color: 'var(--brand-teal)' }}>28+</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Partner Companies</div>
            </div>
            <div style={{ textAlign: 'center', padding: '20px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ fontSize: '28px', fontWeight: '600', color: 'var(--brand-teal)' }}>Pan India</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Coverage</div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            color: 'var(--brand-teal)', 
            borderBottom: '2px solid var(--brand-orange)',
            paddingBottom: '8px',
            marginBottom: '16px'
          }}>
            Our Services
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px', borderLeft: '4px solid var(--brand-teal)' }}>
              <h3 style={{ fontSize: '16px', color: 'var(--brand-teal)', marginBottom: '8px' }}>Design & Engineering</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Conceptual planning, feasibility studies, architectural design as per NMC, NABH, ICMR, HTM, NFPA, ISO, IPHS, NBC standards.
              </p>
            </div>
            <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px', borderLeft: '4px solid var(--brand-orange)' }}>
              <h3 style={{ fontSize: '16px', color: 'var(--brand-orange)', marginBottom: '8px' }}>EPC & Construction</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Engineering, Procurement & Construction of MEP and modular healthcare infrastructure including Operation Theatres, ICUs.
              </p>
            </div>
            <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px', borderLeft: '4px solid var(--brand-teal)' }}>
              <h3 style={{ fontSize: '16px', color: 'var(--brand-teal)', marginBottom: '8px' }}>Medical Equipment & IT</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Complete planning, procurement, and implementation of medical equipment and IT systems for modern healthcare facilities.
              </p>
            </div>
            <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px', borderLeft: '4px solid var(--brand-orange)' }}>
              <h3 style={{ fontSize: '16px', color: 'var(--brand-orange)', marginBottom: '8px' }}>Operations & Management</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Hospital operations including complete medical, paramedical, technical, administrative, and facility staffing.
              </p>
            </div>
          </div>
        </section>

        {/* Product Categories */}
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            color: 'var(--brand-teal)', 
            borderBottom: '2px solid var(--brand-orange)',
            paddingBottom: '8px',
            marginBottom: '16px'
          }}>
            Product Categories (18 Specializations)
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {[
              'Hospital Infrastructure', 'Medical Furniture', 'Radiology', 'Pathology',
              'Surgery & Critical Care', 'Gynecology', 'Oncology', 'Cardiology',
              'Paediatrics & Neonatology', 'Nephrology & Urology', 'Orthopaedics & Neurology',
              'Ophthalmology & ENT', 'Sports Physiotherapy', 'NGO & CSR Projects',
              'NCD Cell', 'Modular Clean Rooms', 'Pharmacy & Medical Stores', 'Reproductive Health'
            ].map((item, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '8px 12px',
                background: 'var(--bg-secondary)',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                <CheckCircle2 size={14} style={{ color: 'var(--brand-teal)', flexShrink: 0 }} />
                <span style={{ color: 'var(--text-primary)' }}>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Leadership Team */}
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            color: 'var(--brand-teal)', 
            borderBottom: '2px solid var(--brand-orange)',
            paddingBottom: '8px',
            marginBottom: '16px'
          }}>
            Leadership Team
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div style={{ textAlign: 'center', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'var(--brand-teal)', 
                margin: '0 auto 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Users size={32} style={{ color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px' }}>Gulshan Bakhtiani</h3>
              <p style={{ fontSize: '12px', color: 'var(--brand-orange)' }}>Founder Director</p>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'var(--brand-teal)', 
                margin: '0 auto 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Users size={32} style={{ color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px' }}>Mr. Mohan Chavan</h3>
              <p style={{ fontSize: '12px', color: 'var(--brand-orange)' }}>Founder-Director</p>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'var(--brand-teal)', 
                margin: '0 auto 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Users size={32} style={{ color: 'white' }} />
              </div>
              <h3 style={{ fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px' }}>Mr. Amol Chavan</h3>
              <p style={{ fontSize: '12px', color: 'var(--brand-orange)' }}>Founder Director</p>
            </div>
          </div>
        </section>

        {/* Major Customers */}
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            color: 'var(--brand-teal)', 
            borderBottom: '2px solid var(--brand-orange)',
            paddingBottom: '8px',
            marginBottom: '16px'
          }}>
            Major Customers
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {[
              'Maharashtra Medical Goods Procurement Authority (MMGPA)',
              'Directorate of Health Services (DHS), Maharashtra',
              'HLL Lifecare Limited',
              'Disaster Management Department, Govt. of Maharashtra',
              'State Public Health Laboratory, Maharashtra',
              'Maharashtra Institute of Virology, Pune',
              'Navi Mumbai Municipal Corporation (NMMC)',
              'Municipal Corporation of Greater Mumbai (MCGM)',
              'Thane Municipal Corporation (TMC)',
              'Lilavati Hospital and Research Centre',
              'Railway Hospitals (Indian Railways)',
              'BARC Hospital, Mumbai'
            ].map((item, index) => (
              <div key={index} style={{ 
                padding: '8px 12px',
                background: 'var(--bg-secondary)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'var(--text-secondary)',
                borderLeft: '3px solid var(--brand-teal)'
              }}>
                {item}
              </div>
            ))}
          </div>
        </section>

        {/* Compliance */}
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            color: 'var(--brand-teal)', 
            borderBottom: '2px solid var(--brand-orange)',
            paddingBottom: '8px',
            marginBottom: '16px'
          }}>
            Compliance & Standards
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {['NMC', 'NABH', 'ICMR', 'HTM', 'NFPA', 'ISO', 'IPHS', 'NBC'].map((item, index) => (
              <div key={index} style={{ 
                padding: '10px 20px',
                background: 'var(--brand-teal)',
                color: 'white',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                {item}
              </div>
            ))}
          </div>
        </section>

        {/* Contact Information */}
        <section style={{ 
          marginBottom: '40px', 
          background: 'linear-gradient(135deg, var(--brand-teal) 0%, #006666 100%)',
          padding: '32px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <h2 style={{ 
            fontSize: '24px', 
            color: 'white', 
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Contact Us
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', textAlign: 'center' }}>
            <div>
              <MapPin size={24} style={{ marginBottom: '8px' }} />
              <p style={{ fontSize: '13px', lineHeight: '1.6' }}>
                3A & 3B, 3rd Floor, Trust House,<br />
                35 Hospital Avenue, Dr. E. Borges Road,<br />
                Parel, Mumbai - 400012
              </p>
            </div>
            <div>
              <Phone size={24} style={{ marginBottom: '8px' }} />
              <p style={{ fontSize: '14px' }}>+91 22 4579 6459</p>
            </div>
            <div>
              <Mail size={24} style={{ marginBottom: '8px' }} />
              <p style={{ fontSize: '14px' }}>info@pinnaclebiomed.in</p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div style={{ textAlign: 'center', paddingTop: '24px', borderTop: '2px solid var(--border-light)' }}>
          <p style={{ fontSize: '12px', color: 'var(--text-light)' }}>
            © 2024 Pinnacle Biomed Pvt. Ltd. All Rights Reserved.
          </p>
          <p style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
            Affecting Lives.. Positively.
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .company-profile-page {
            padding: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CompanyProfile;
